<template>
  <Dialog
    v-model:visible="visibleModel"
    :header="isEditing ? 'Edit Special Hours' : 'Set Special Hours'"
    modal
    :style="{ width: '400px' }"
    @hide="onHide"
  >
    <div class="dialog-content">
      <div class="field">
        <label>Date</label>
        <Calendar
          v-model="form.date"
          dateFormat="yy-mm-dd"
          :disabled="isEditing"
          class="w-full"
        />
      </div>

      <div class="field">
        <label>Status</label>
        <SelectButton
          v-model="form.is_closed"
          :options="statusOptions"
          optionLabel="label"
          optionValue="value"
        />
      </div>

      <template v-if="!form.is_closed">
        <div class="field">
          <label>Open Time</label>
          <InputText
            v-model="form.open_time"
            placeholder="09:00"
            maxlength="5"
            class="w-full"
            @blur="validateTime('open_time')"
          />
          <small class="p-error" v-if="errors.open_time">
            {{ errors.open_time }}
          </small>
        </div>
        <div class="field">
          <label>Close Time</label>
          <InputText
            v-model="form.close_time"
            placeholder="17:00"
            maxlength="5"
            class="w-full"
            @blur="validateTime('close_time')"
          />
          <small class="p-error" v-if="errors.close_time">
            {{ errors.close_time }}
          </small>
        </div>
      </template>

      <div class="field">
        <label>Note (optional)</label>
        <InputText
          v-model="form.note"
          placeholder="e.g., Thanksgiving"
          maxlength="100"
          class="w-full"
        />
      </div>
    </div>

    <template #footer>
      <Button label="Cancel" text @click="visibleModel = false" />
      <Button label="Save" @click="save" :loading="saving" />
    </template>
  </Dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { format, parseISO } from 'date-fns'
import { useToast } from 'primevue/usetoast'
import { createHolidayHours, updateHolidayHours } from '@/lib/api'

const props = defineProps({
  visible: { type: Boolean, default: false },
  storeId: { type: Number, required: true },
  date: { type: String, default: null },  // YYYY-MM-DD
  holidayData: { type: Object, default: null }  // For editing existing
})

const emit = defineEmits(['update:visible', 'saved'])

const toast = useToast()
const saving = ref(false)

// Two-way binding for visible prop
const visibleModel = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value)
})

const isEditing = computed(() => !!props.holidayData?.id)

const statusOptions = [
  { label: 'Closed', value: true },
  { label: 'Modified Hours', value: false }
]

const defaultForm = () => ({
  date: props.date ? parseISO(props.date) : new Date(),
  is_closed: true,
  open_time: '',
  close_time: '',
  note: ''
})

const form = ref(defaultForm())
const errors = ref({
  open_time: '',
  close_time: ''
})

// Time validation regex (HH:MM format)
const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/

const validateTime = (field) => {
  const value = form.value[field]
  if (!value || value.trim() === '') {
    errors.value[field] = ''
    return true
  }

  if (!timeRegex.test(value)) {
    errors.value[field] = 'Invalid time format (use HH:MM)'
    return false
  }

  errors.value[field] = ''
  return true
}

// Reset form when dialog opens or props change
watch([visibleModel, () => props.date, () => props.holidayData], () => {
  if (visibleModel.value) {
    errors.value = { open_time: '', close_time: '' }

    if (props.holidayData) {
      form.value = {
        id: props.holidayData.id,
        date: parseISO(props.holidayData.date),
        is_closed: props.holidayData.is_closed,
        open_time: props.holidayData.open_time?.slice(0, 5) || '',
        close_time: props.holidayData.close_time?.slice(0, 5) || '',
        note: props.holidayData.note || ''
      }
    } else {
      form.value = defaultForm()
    }
  }
}, { immediate: true })

const save = async () => {
  // Validate required fields
  if (!form.value.date) {
    toast.add({
      severity: 'error',
      summary: 'Validation Error',
      detail: 'Date is required',
      life: 3000
    })
    return
  }

  // Validate times if not closed
  if (!form.value.is_closed) {
    const openValid = validateTime('open_time')
    const closeValid = validateTime('close_time')

    if (!form.value.open_time || !form.value.close_time) {
      toast.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Open and close times are required for modified hours',
        life: 3000
      })
      return
    }

    if (!openValid || !closeValid) {
      toast.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please enter valid times in HH:MM format',
        life: 3000
      })
      return
    }
  }

  const payload = {
    store_id: props.storeId,
    date: format(form.value.date, 'yyyy-MM-dd'),
    is_closed: form.value.is_closed,
    open_time: form.value.is_closed ? null : form.value.open_time,
    close_time: form.value.is_closed ? null : form.value.close_time,
    note: form.value.note || null
  }

  saving.value = true
  try {
    if (isEditing.value) {
      await updateHolidayHours(form.value.id, payload)
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Special hours updated',
        life: 3000
      })
    } else {
      await createHolidayHours(payload)
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Special hours added',
        life: 3000
      })
    }

    visibleModel.value = false
    emit('saved')
  } catch (error) {
    const msg = error.response?.data?.detail || 'Failed to save special hours'
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: msg,
      life: 5000
    })
  } finally {
    saving.value = false
  }
}

const onHide = () => {
  form.value = defaultForm()
  errors.value = { open_time: '', close_time: '' }
}
</script>

<style scoped>
.dialog-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-top: 1rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.field label {
  font-weight: 500;
}

.w-full {
  width: 100%;
}

.p-error {
  color: #ef4444;
  font-size: 0.875rem;
}
</style>
